import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, FileText, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeeStructures() {
  const { token } = useAuth();
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    academic_year: '2025',
    term: 'Term 1',
    class_id: '',
    fee_items: []
  });
  const [newItem, setNewItem] = useState({ name: '', amount: '', is_optional: false });

  useEffect(() => {
    fetchStructures();
    fetchClasses();
  }, []);

  const fetchStructures = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finance/fee-structures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStructures(data);
    } catch (error) {
      toast.error('Failed to fetch fee structures');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await fetch(`${API_URL}/api/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setClasses(data);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.fee_items.length === 0) {
      toast.error('Please add at least one fee item');
      return;
    }

    try {
      const url = editingStructure 
        ? `${API_URL}/api/finance/fee-structures/${editingStructure.id}`
        : `${API_URL}/api/finance/fee-structures`;
      
      const res = await fetch(url, {
        method: editingStructure ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          class_id: formData.class_id || null
        })
      });

      if (res.ok) {
        toast.success(editingStructure ? 'Fee structure updated' : 'Fee structure created');
        setShowDialog(false);
        resetForm();
        fetchStructures();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save fee structure');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this fee structure?')) return;

    try {
      const res = await fetch(`${API_URL}/api/finance/fee-structures/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Fee structure deleted');
        fetchStructures();
      }
    } catch (error) {
      toast.error('Failed to delete fee structure');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      academic_year: '2025',
      term: 'Term 1',
      class_id: '',
      fee_items: []
    });
    setEditingStructure(null);
    setNewItem({ name: '', amount: '', is_optional: false });
  };

  const addFeeItem = () => {
    if (!newItem.name || !newItem.amount) {
      toast.error('Please fill in item name and amount');
      return;
    }

    setFormData(prev => ({
      ...prev,
      fee_items: [...prev.fee_items, { ...newItem, amount: parseFloat(newItem.amount) }]
    }));
    setNewItem({ name: '', amount: '', is_optional: false });
  };

  const removeFeeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      fee_items: prev.fee_items.filter((_, i) => i !== index)
    }));
  };

  const openEdit = (structure) => {
    setEditingStructure(structure);
    setFormData({
      name: structure.name,
      academic_year: structure.academic_year,
      term: structure.term,
      class_id: structure.class_id || '',
      fee_items: structure.fee_items.map(item => ({
        name: item.name,
        amount: item.amount,
        is_optional: item.is_optional,
        description: item.description
      }))
    });
    setShowDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="fee-structures-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Fee Structures</h1>
          <p className="text-slate-500">Manage fee structures per class and term</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-fee-structure-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Fee Structure
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {structures.map(structure => (
          <Card key={structure.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{structure.name}</CardTitle>
                  <p className="text-sm text-slate-500">
                    {structure.academic_year} - {structure.term}
                  </p>
                </div>
                <Badge variant={structure.is_active ? "default" : "secondary"}>
                  {structure.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Class:</span>
                  <span className="font-medium">{structure.class_name || 'All Classes'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Items:</span>
                  <span className="font-medium">{structure.fee_items.length} items</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">Total:</span>
                    <span className="text-lg font-bold text-indigo-600">
                      {formatCurrency(structure.total_amount)}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(structure)}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(structure.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {structures.length === 0 && !loading && (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700">No Fee Structures</h3>
          <p className="text-slate-500 mb-4">Create your first fee structure to get started</p>
          <Button onClick={() => setShowDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Fee Structure
          </Button>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStructure ? 'Edit Fee Structure' : 'Create Fee Structure'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Structure Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Term 1 2025 Fees"
                  required
                />
              </div>
              <div>
                <Label>Academic Year</Label>
                <Select
                  value={formData.academic_year}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, academic_year: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Term</Label>
                <Select
                  value={formData.term}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, term: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Term 1">Term 1</SelectItem>
                    <SelectItem value="Term 2">Term 2</SelectItem>
                    <SelectItem value="Term 3">Term 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Class (Optional)</Label>
                <Select
                  value={formData.class_id || "all"}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, class_id: value === "all" ? "" : value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Classes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {classes.map(cls => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Fee Items */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Fee Items</Label>
              
              <div className="flex gap-2">
                <Input
                  placeholder="Item name (e.g., Tuition)"
                  value={newItem.name}
                  onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                  className="flex-1"
                />
                <Input
                  type="number"
                  placeholder="Amount"
                  value={newItem.amount}
                  onChange={(e) => setNewItem(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-32"
                />
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={newItem.is_optional}
                    onChange={(e) => setNewItem(prev => ({ ...prev, is_optional: e.target.checked }))}
                  />
                  Optional
                </label>
                <Button type="button" onClick={addFeeItem} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {formData.fee_items.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center">Optional</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.fee_items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                        <TableCell className="text-center">
                          {item.is_optional ? <Badge variant="secondary">Yes</Badge> : '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFeeItem(index)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-50">
                      <TableCell className="font-bold">Total</TableCell>
                      <TableCell className="text-right font-bold text-indigo-600">
                        {formatCurrency(formData.fee_items.reduce((sum, item) => sum + item.amount, 0))}
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-fee-structure-btn">
                {editingStructure ? 'Update' : 'Create'} Fee Structure
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
